package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.browser.window
import org.example.project.dashboard.ScreenManager
import org.example.project.enginetabular.META_HEADERS
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.service.RollenService
import org.example.project.utils.All
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

@Composable
fun MeineLeads(node: String = "SYS") {
    var eintraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var headers by remember { mutableStateOf<List<VertriebsindexApi.ZusatzHeader>>(emptyList()) }
    var filters by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var selectedIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    var infoLeadId by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val sharedKpis = LocalLinkedInKpis.current
    var sourceFilter by remember { mutableStateOf("Alle") }
    var connectionOpen by remember { mutableStateOf(false) }
    var leadTypen by remember { mutableStateOf<List<VertriebsindexApi.LeadTypEintrag>>(emptyList()) }
    var leadTypenOpen by remember { mutableStateOf(false) }
    fun rowKey(lead: All) = "${lead.leadQuelle}_${lead.id}"
    val currentEntries = eintraege.map { lead ->
        val live = if (lead.leadQuelle == "LinkedIn") sharedKpis?.contacts?.firstOrNull { it.linkedinProspectId == lead.id } else null
        if (live == null) lead else lead.copy(linkedinHatNachrichten = live.linkedinHatNachrichten,
            linkedinNetzwerkstatus = live.linkedinNetzwerkstatus, vertriebCheckpoint = live.vertriebCheckpoint)
    }.filter { it.leadQuelle != "LinkedIn" || it.vertriebCheckpoint in setOf("", "MY_LEADS") }
    val visible = currentEntries.filter { sourceFilter == "Alle" || it.leadQuelle == sourceFilter }
    val selected = visible.filter { rowKey(it) in selectedIds }
    val canClassify = !busy && RollenService.hasActionPermission("MeineLeads", RollenService.ACTION_EDIT)
    val typeSelection = selected.isNotEmpty() && selected.size == selectedIds.size && selected.all {
        it.leadQuelle == "LinkedIn" && it.vertriebCheckpoint in setOf("", "MY_LEADS")
    }
    val canReturn = !busy && selected.isNotEmpty() && selected.size == selectedIds.size &&
        selected.all { it.leadQuelle == "Web" } &&
        selected.none { it.vertriebCheckpoint == "CLOSED" } &&
        selected.none { it.status in setOf("Storniert", "Abgeschlossen", "Abgebrochen (negativ)") } &&
        RollenService.hasActionPermission("MeineLeads", RollenService.ACTION_EDIT)

    suspend fun reload() {
        headers = VertriebsindexApi.loadHeaders()
        leadTypen = VertriebsindexApi.loadLeadtypen()
        eintraege = VertriebsindexApi.searchMeineLeads(filters).sortedBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
        selectedIds = selectedIds.intersect(eintraege.map { rowKey(it) }.toSet())
    }
    fun work(action: suspend () -> Unit) {
        if (busy) return
        busy = true
        error = ""
        scope.launch {
            try { action() }
            catch (e: CancellationException) { throw e }
            catch (e: Exception) { error = e.message ?: "Aktion fehlgeschlagen. Bitte aktualisieren." }
            finally { busy = false }
        }
    }
    LaunchedEffect(LinkedInSyncState.version) { work { reload() } }
    val infoLead = eintraege.firstOrNull { rowKey(it) == infoLeadId }
    if (infoLead != null) LeadInfoReadOnlyPopup(infoLead) { infoLeadId = null }
    if (leadTypenOpen) VertriebLeadtypenDialog(onClose = { leadTypenOpen = false }, onChanged = {
        leadTypen = it
        work { reload(); LinkedInSyncState.version++ }
    }, rollenKey = "MeineLeads")
    if (error.isNotBlank()) Div({ attr("role", "alert") }) { Text(error) }

    TabularScreen(
        screenKey = "MeineLeads", rollenKey = "MeineLeads", title = "Meine Leads",
        fixedLeadingColumns = vertriebFixedLeadingHeaders().take(4),
        fixedTrailingColumns = META_HEADERS,
        headers = vertriebTabularHeaders(headers) + listOf("linkedinNetzwerkstatus", "linkedinVernetztAm", "linkedinHatNachrichten"),
        headerDisplayLabels = vertriebTabularLabels(headers) + listOf("Netzwerkstatus", "Vernetzt seit", "Chat"),
        toggleColumns = headers.filter { it.typ == "CHECKBOX" }.map { it.id }.toSet(),
        models = visible.map { it.copy(id = rowKey(it)) },
        data = visible.map { rowKey(it) to (vertriebRow(it, headers) + if (it.leadQuelle == "LinkedIn")
            listOf(it.linkedinNetzwerkstatus, linkedInConnectedDate(it.linkedinVernetztAm), if (it.linkedinHatNachrichten) "Ja" else "Nein") else listOf("", "", "")) },
        onSelectedRowsChange = { selectedIds = it },
        editable = false, allowCreate = false, allowSave = false, allowDelete = false,
        allowImportButton = false, allowExportButton = false, allowSettingsButton = false,
        extraNavButtons = listOf(
            TabularToolbarButton("Lead-Typ", "images/filter.svg", canClassify,
                dropdownOptions = listOf("__manage__" to "Leadtypen verwalten") + leadTypen.map { it.leadTyp to it.leadTyp },
                selectedDropdownValue = selected.map { it.leadTyp }.distinct().singleOrNull(),
                dropdownPlaceholder = "Lead-Typ zuweisen", onDropdownChange = { value ->
                    if (value == "__manage__") leadTypenOpen = true
                    else if (value != null) {
                        if (!typeSelection) error = "Bitte LinkedIn-Leads im Zustand Meine Leads markieren."
                        else { val ids = selected.map { it.id }; work {
                            VertriebsindexApi.setLeadTyp(ids, value, "MINE"); reload(); LinkedInSyncState.version++
                        } }
                    }
                }),
            TabularToolbarButton("Leadquelle", "images/filter.svg", !busy,
                dropdownOptions = listOf("Alle", "Web", "LinkedIn").map { it to it }, dropdownPlaceholder = sourceFilter,
                onDropdownChange = { if (it != null) { sourceFilter = it; selectedIds = emptySet() } }),
            TabularToolbarButton("Lead-Info", "images/info.svg", !busy && selected.size == 1, onClick = {
                infoLeadId = selected.singleOrNull()?.let { rowKey(it) }
            }),
            TabularToolbarButton("Im Flow öffnen", "images/call.svg", !busy && selected.size == 1 &&
                RollenService.hasReadPermission("LinkedInFlow"), onClick = {
                val lead = selected.singleOrNull()
                if (lead != null) work {
                    val screen = if (node == "SYS" && ScreenManager.isLinkedInNode()) "SYSVertriebsDashboard" else "VertriebsDashboard"
                    val query = run {
                        val contact = sharedKpis?.contacts?.firstOrNull { it.linkedinProspectId == lead.id && it.leadQuelle == lead.leadQuelle }
                            ?: LinkedInFlowApi.chat().linkedinKontakte.firstOrNull { it.linkedinProspectId == lead.id && it.leadQuelle == lead.leadQuelle }
                            ?: error("Kontakt wurde nicht gefunden.")
                        "linkedinContact=" + (js("encodeURIComponent")(contact.id) as String)
                    }
                    window.location.hash = window.location.hash.substringBefore('?') + "?screen=$screen&$query&salesNavigation=${kotlin.js.Date.now().toLong()}"
                }
            }),
            TabularToolbarButton("Zurück in den Pool", "images/back.svg", canReturn, onClick = {
                if (canReturn) {
                    val ids = selected.map { it.id }
                    val rowKeys = selected.map { rowKey(it) }.toSet()
                    work {
                        VertriebsindexApi.returnMeineLeadsToPool(ids)
                        eintraege = eintraege.filterNot { rowKey(it) in rowKeys }
                        selectedIds = emptySet()
                    }
                }
            }),
            TabularToolbarButton("LinkedIn verbinden", "images/settings.svg", !busy && ScreenManager.isLinkedInNode() &&
                RollenService.hasActionPermission("MeineLeads", RollenService.ACTION_SETTINGS), onClick = { connectionOpen = true })
        ),
        onRefresh = { work { filters = emptyMap(); reload() } },
        onSearchWithFilters = { nextFilters -> work { filters = nextFilters; reload() } },
        allowSearchButton = true, showFilterRow = true, onCommit = {}
    )
    if (connectionOpen) LinkedInConnectionPopup(onSaved = { work { reload() } }, onClose = { connectionOpen = false })
}
